import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Avatar, Box, Button, Chip, IconButton, Skeleton, Switch, Typography } from '@mui/material';
import { MdArrowBack, MdEdit, MdInventory2 } from 'react-icons/md';
import { getComboById, updateCombo } from '../../Webservice/offerAPI';
import { FONT, StateChip, fmtDate, imageUrl, period, stateOf, yellowBtn } from './bogoShared';
import { ImageGallery } from './OfferImages';
import { MixNotice, PriceBreakdown, StockChip, inr, isSeller, stockInfo, totalsFor } from './comboPricing';

const Card = ({ title, accent, children, right }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15 }}>{title}</Typography>
      {right}
    </Box>
    {children}
  </Box>
);
const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);

function ViewComboOffer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await getComboById(id);
      const c = res?.data || res?.combo;
      if (!c) throw new Error('missing');
      setCombo(c);
    } catch {
      setError('This combo could not be found.');
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    try {
      await updateCombo(id, { isActive: !combo.isActive });
      load();
    } catch { /* the service shows the error */ }
  };

  if (loading) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={140} sx={{ borderRadius: '14px', mb: 2 }} /><Skeleton variant="rounded" height={320} sx={{ borderRadius: '14px' }} /></Box>;
  if (error || !combo) {
    return <Box sx={{ p: 3, maxWidth: 520, mx: 'auto' }}><Alert severity="error" sx={{ mb: 2 }}>{error}</Alert><Button onClick={() => navigate('/offers/combo')} sx={yellowBtn}>Back to combos</Button></Box>;
  }

  const lines = (combo.products || []).filter((p) => p.product_id && typeof p.product_id === 'object');
  const priced = lines.map((l) => ({ product: l.product_id, quantity: l.quantity || 1 }));
  const totals = totalsFor(priced);
  const mrp = totals.b2c.mrp;
  const infos = lines.map((l) => stockInfo(l.product_id, l.quantity || 1));
  const blockers = lines.map((l, i) => ({ title: l.product_id.title, info: infos[i] })).filter((x) => x.info.state === 'out');
  const lowOnes = infos.filter((x) => x.state === 'low').length;
  const price = combo.b2c_sellingPrice || 0;
  const savePct = mrp > price && price > 0 ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const image = combo.product_images?.[0];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
        <IconButton onClick={() => navigate('/offers/combo')} sx={{ background: '#f1f5f9' }}><MdArrowBack size={18} /></IconButton>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>{combo.title}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <StateChip state={stateOf(combo)} />
            <Chip size="small" label={combo.inStock ? `${combo.availableQuantity} available` : 'Out of stock'} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: combo.inStock ? '#dcfce7' : '#fee2e2', color: combo.inStock ? '#166534' : '#991b1b' }} />
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{combo.comboCode} · {period(combo)}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>{combo.isActive ? 'On' : 'Off'}</Typography>
          <Switch checked={!!combo.isActive} onChange={toggle} />
        </Box>
        <Button startIcon={<MdEdit />} variant="contained" onClick={() => navigate(`/offers/combo/edit/${id}`)} sx={yellowBtn}>Edit combo</Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center', flexWrap: 'wrap', p: 2.5, borderRadius: '16px', mb: 2.5, color: '#fff', background: 'linear-gradient(135deg,#6366f1,#4338ca)' }}>
        <Avatar variant="rounded" src={imageUrl(image)} sx={{ width: 88, height: 88, bgcolor: 'rgba(255,255,255,.2)' }}><MdInventory2 size={34} /></Avatar>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.85, fontWeight: 700 }}>{lines.length} PRODUCT{lines.length === 1 ? '' : 'S'} IN ONE BUNDLE</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, opacity: 0.95, mt: 0.5 }}>{combo.description || 'No description.'}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 32, fontWeight: 800, lineHeight: 1.1 }}>{price ? inr(price) : '—'}</Typography>
          {mrp > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13.5, opacity: 0.85 }}><span style={{ textDecoration: 'line-through' }}>{inr(mrp)}</span>{savePct ? ` · ${savePct}% off` : ''}</Typography>}
        </Box>
      </Box>

      <MixNotice products={lines.map((l) => l.product_id)} sx={{ mb: 2.5 }} />
      {blockers.length > 0 && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          <b>This combo shows Out of stock</b> because of: {blockers.map((b) => `${b.title} (${b.info.why})`).join('; ')}.
        </Alert>
      )}
      {blockers.length === 0 && !combo.inStock && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>The combo is switched off, so customers cannot buy it. All products have stock.</Alert>
      )}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1.5fr' }, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card title="Pictures" accent="#ec4899">
            <ImageGallery images={combo.product_images || []} />
          </Card>
          <Card title="Stock" accent="#ef4444">
            <Row label="Combos you can sell" value={combo.inStock ? combo.availableQuantity : '0'} />
            <Row label="Products available" value={`${infos.filter((x) => x.state !== 'out').length} of ${infos.length}`} />
            <Row label="Low on stock" value={lowOnes} />
            <Row label="Blocking the combo" value={blockers.length} />
          </Card>
          <Card title="Availability" accent="#0ea5e9">
            <Row label="Starts" value={combo.startDate ? fmtDate(combo.startDate) : 'Immediately'} />
            <Row label="Ends" value={combo.endDate ? fmtDate(combo.endDate) : 'No end date'} />
            <Row label="Visible to" value={(combo.visibility || []).map((v) => <Chip key={v} size="small" label={v.toUpperCase()} sx={{ ml: 0.5, height: 20, fontWeight: 700 }} />)} />
            <Row label="Created" value={fmtDate(combo.createdAt)} />
            <Row label="Last updated" value={fmtDate(combo.updatedAt)} />
          </Card>
        </Box>

        <Card title="What is inside" accent="#f59e0b" right={<Chip size="small" label={lines.length} sx={{ fontWeight: 800, background: 'rgb(255 209 2)' }} />}>
          {lines.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', py: 3, textAlign: 'center' }}>No products in this combo.</Typography>}
          {lines.map((l, i) => {
            const p = l.product_id;
            const q = l.quantity || 1;
            const info = infos[i];
            return (
              <Box key={p._id || i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.1, borderBottom: i < lines.length - 1 ? '1px dashed #e2e8f0' : 'none', flexWrap: 'wrap' }}>
                <Avatar variant="rounded" src={imageUrl(p.product_images?.[0])} sx={{ width: 48, height: 48, bgcolor: '#e0e7ff', color: '#3730a3' }}><MdInventory2 /></Avatar>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5 }}>{p.title} <Chip size="small" label={isSeller(p) ? 'Seller' : 'Own'} sx={{ ml: 0.5, height: 18, fontSize: 10.5, fontWeight: 800, background: isSeller(p) ? '#ede9fe' : '#e0f2fe', color: isSeller(p) ? '#5b21b6' : '#075985' }} /></Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{p.productCode || ''}{p.brand ? ` · ${p.brand}` : ''} · {info.stock} in stock · {info.why}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#475569' }}>MRP {inr(p.b2c_mrp)} · B2C {inr(p.b2c_sellingPrice || p.b2c_mrp)} · B2B {Number(p.b2b_sellingPrice) ? inr(p.b2b_sellingPrice) : 'not set'}</Typography>
                </Box>
                <StockChip info={info} />
                <Chip size="small" label={`× ${q}`} sx={{ fontWeight: 800 }} />
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, minWidth: 74, textAlign: 'right' }}>{inr((Number(p.b2c_sellingPrice) || Number(p.b2c_mrp) || 0) * q)}</Typography>
              </Box>
            );
          })}
        </Card>
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <Card title="Price breakdown" accent="#22c55e">
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mb: 1.5 }}>MRP and the cost of buying each product on its own, for customers (B2C) and business buyers (B2B).</Typography>
          <PriceBreakdown lines={priced} b2cPrice={combo.b2c_sellingPrice} b2bPrice={combo.b2b_sellingPrice} />
        </Card>
      </Box>
    </Box>
  );
}

export default ViewComboOffer;
